// "Reply without dread" (U5): holding replies, tone and length rewrites and the second-snooze offer
// (/api/unstick), letting a card go with its reason, a dictated draft instruction, and the
// dictation setting (/api/settings/dictation).
import { useQuery } from "@tanstack/react-query";
import type { CardView, DraftRequestView } from "@shared/api";
import type {
  DictationSetting,
  HoldingReplyView,
  RewriteResult,
  SmallerStepChoice,
  Tone,
} from "@shared/unstick";
import { api } from "./api";
import { feedUrls } from "./feedApi";

const enc = encodeURIComponent;
const holdingUrl = (cardId: string) => `/api/unstick/cards/${enc(cardId)}/holding-reply`;
const DICTATION_URL = "/api/settings/dictation";

export interface HoldingResult {
  card: CardView;
  holding: HoldingReplyView;
}

export const unstickApi = {
  holding: (cardId: string, signal?: AbortSignal) =>
    api<{ holding: HoldingReplyView | null }>(holdingUrl(cardId), { signal }),
  draftHolding: (cardId: string, day?: string) =>
    api<HoldingResult>(holdingUrl(cardId), { method: "POST", body: day ? { day } : {} }),
  changeHoldingDay: (cardId: string, day: string, text: string) =>
    api<HoldingResult>(holdingUrl(cardId), { method: "PUT", body: { day, text } }),
  removeHolding: (cardId: string) =>
    api<{ card: CardView }>(holdingUrl(cardId), { method: "DELETE" }),
  rewrite: (body: { text: string; tone: Tone; cardId?: string; accountId?: string }) =>
    api<RewriteResult>("/api/unstick/rewrite", { method: "POST", body }),
  smallerStep: (cardId: string, choice: SmallerStepChoice) =>
    api<{ card: CardView }>(`/api/unstick/cards/${enc(cardId)}/smaller-step`, {
      method: "POST",
      body: { choice },
    }),
  /** Dismiss with the reason kept (U5 "Let it go"). */
  dismiss: (cardId: string, reason: string) =>
    api<CardView>(feedUrls.dismiss(cardId), { method: "POST", body: { reason } }),
  /** The owner's own draft instruction for a card (a dictated reply). */
  draftFromInstruction: (cardId: string, instruction: string) =>
    api<DraftRequestView>(feedUrls.draft(cardId), { method: "POST", body: { instruction } }),
  dictation: (signal?: AbortSignal) => api<DictationSetting>(DICTATION_URL, { signal }),
  saveDictation: (on: boolean) =>
    api<DictationSetting>(DICTATION_URL, { method: "PUT", body: { on } }),
};

export const unstickKeys = {
  holding: (cardId: string) => ["unstick", "holding", cardId] as const,
  dictation: ["settings", "dictation"] as const,
};

/** The card's holding reply (its day and the days to choose from), while it has one. */
export function useHoldingReply(cardId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: unstickKeys.holding(cardId ?? ""),
    queryFn: ({ signal }) => unstickApi.holding(cardId!, signal),
    enabled: !!cardId && enabled,
    staleTime: 30_000,
  });
}

export function useDictationSetting() {
  return useQuery({
    queryKey: unstickKeys.dictation,
    queryFn: ({ signal }) => unstickApi.dictation(signal),
    staleTime: 5 * 60_000,
  });
}
